import React, { useEffect, useRef, useState } from "react";
// ANT DESIGN COMPONENTS
import { Cascader, Dropdown, Tooltip, Button } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBan,
  faCheckDouble,
  faList,
  faStar,
} from "@fortawesome/free-solid-svg-icons";
// REDUX
import { useDispatch, useSelector } from "react-redux";
import {
  useLazyGetFavoriteGridQuery,
  useLazyGetOrganizationsCascaderQuery,
  useLazyGetFavoriteGridsByOrganizationQuery,
} from "../../../redux/monitoring/monitoringAPI";
import {
  updateCascaderSelectMultipleSelectionOptions,
  updateTopViewLayout,
  updateDetailViewLayout,
  updateStatusViewLayout,
  updateIdGridSelected,
  updateFavoriteGridName,
  updateClearCascader,
  updateDoorUnknownAsOpening,
  updateHideUnconfiguredDoors,
} from "../../../redux/monitoring/monitoringSlice";
import { updateOrganizationSelectMultipleData } from "../../../redux/organizations/organizationSlice";
// AUTHORIZATION
import GETJwtToken from "../../../redux/authentication/authentication";
import {
  updateFloorsEnabled,
  updateNamesEnabled,
  updateServiceModeEnabled,
  updateShowOriginal,
} from "../../../redux/mapView/mapViewSlice";
// INTERFACES
import { RootState } from "../../../app/store";

const CASCADER_STORAGE_KEY = "lnMonitoringCascaderSelection";

function OrganizationCascaderSelectMultiple() {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const { SHOW_CHILD } = Cascader;
  const dispatch = useDispatch();
  const [t] = useTranslation("global");

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [cascaderData, setCascaderData] = useState([]);
  const [cascaderValue, setCascaderValue] = useState<string[]>([]);
  const [listFavoriteGrids, setListFavoriteGrids] = useState<any>([]);

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { cascaderSelectMultipleSelectionOptions, refreshPage, clearCascader } =
    useSelector((state: RootState) => state.monitoring);
  const { theme, userLogged, globalOrganizationId } = useSelector(
    (state: RootState) => state.home
  );

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */
  const [
    triggerGetOrganizationsCascader,
    {
      data: dataGetOrganizationsCascader,
      isLoading: isLoadingOrganizationCascader,
    },
  ] = useLazyGetOrganizationsCascaderQuery();

  const [
    triggerGetGridById,
    { data: dataGridById, isLoading: isLoadingGridById },
  ] = useLazyGetFavoriteGridQuery();

  const [
    triggerGetFavoriteGridsByOrganization,
    { data: dataGetFavoriteGrids, isLoading: isLoadingFavoriteGrids },
  ] = useLazyGetFavoriteGridsByOrganizationQuery();

  // ************************************************ */
  // FUNCTIONS ************************************** */

  /** Returns the cascaderData filtered by org for non-master users */
  const getVisibleCascaderData = (data: any[]): any[] => {
    if (globalOrganizationId === process.env.REACT_APP_MASTER_ORG_ID) {
      return data;
    }
    return data.filter((item: any) => item.value === globalOrganizationId);
  };

  /** Persist current selection to localStorage */
  const persistSelection = (banks: any[], paths: any[]) => {
    if (!userLogged || !userLogged.id) return;
    localStorage.setItem(
      CASCADER_STORAGE_KEY,
      JSON.stringify({
        userId: userLogged.id,
        organizationSelectMultipleData: banks,
        cascaderSelectMultipleSelectionOptions: paths,
      })
    );
  };

  /** Collect all valid bank IDs from a cascaderData tree */
  const collectBankIds = (data: any[]): Set<string> => {
    const ids = new Set<string>();
    data.forEach((org: any) => {
      if (org.children) {
        org.children.forEach((serv: any) => {
          if (serv.children) {
            serv.children.forEach((chan: any) => {
              if (chan.children) {
                chan.children.forEach((bank: any) => {
                  ids.add(bank.value);
                });
              }
            });
          }
        });
      }
    });
    return ids;
  };

  const initData = async () => {
    const token = await GETJwtToken();
    triggerGetOrganizationsCascader({ token });
    triggerGetFavoriteGridsByOrganization({
      token,
      idOrganization: userLogged.org_id,
    });
    if (
      cascaderSelectMultipleSelectionOptions &&
      cascaderSelectMultipleSelectionOptions.length > 0
    ) {
      setCascaderValue(cascaderSelectMultipleSelectionOptions);
    }
  };

  const buildCascaderArray = () => {
    if (
      dataGetOrganizationsCascader &&
      dataGetOrganizationsCascader.organizations &&
      dataGetOrganizationsCascader.organizations.length > 0
    ) {
      const newArr: any = [];
      const orgNodesById = new Map<string, any>();
      const serverIdsByOrgId = new Map<string, Set<string>>();
      const organizations = dataGetOrganizationsCascader.organizations;

      // ***************************************
      // BUILD ORGANIZATIONS CASCADER (ORG ONLY)
      organizations.map((org: any) => {
        if (org?.type === "SUBORG") {
          return true;
        }
        const orgCascader: any = {
          value: org.id,
          label: org.name,
          children: [],
          type: "organization",
        };
        orgNodesById.set(org.id, orgCascader);
        return newArr.push(orgCascader);
      });

      // ***************************************
      // BUILD SERVERS / CHANNELS / BANKS CASCADER
      organizations.map((org: any) => {
        const parentOrgNode =
          org?.type === "SUBORG" && org?.parent_id
            ? orgNodesById.get(org.parent_id)
            : orgNodesById.get(org.id);

        const targetOrgNode =
          parentOrgNode ||
          ({
            value: org.id,
            label: org.name,
            children: [],
            type: "organization",
          } as any);

        if (!parentOrgNode && !orgNodesById.get(org.id)) {
          orgNodesById.set(org.id, targetOrgNode);
          newArr.push(targetOrgNode);
        }

        if (org && org.servers && org.servers.length > 0) {
          org.servers.map((serv: any) => {
            if (serv && serv.active !== false) {
              const targetOrgId = targetOrgNode.value;
              if (!serverIdsByOrgId.has(targetOrgId)) {
                serverIdsByOrgId.set(targetOrgId, new Set<string>());
              }
              const targetOrgServerIds = serverIdsByOrgId.get(targetOrgId);
              if (targetOrgServerIds && targetOrgServerIds.has(serv.id)) {
                return true;
              }
              targetOrgServerIds?.add(serv.id);

              const servCascader: any = {
                value: serv.id,
                label: serv.name,
                children: [],
                type: "server",
              };
              // ***************************************
              // BUILD CHANNELS CASCADER
              if (serv && serv.channels && serv.channels.length > 0) {
                serv.channels.map((channel: any) => {
                  const channelCascader: any = {
                    value: channel.id,
                    label: channel.name,
                    children: [],
                    type: "channel",
                  };
                  // ***************************************
                  // BUILD BANKS CASCADER
                  if (channel && channel.banks && channel.banks.length > 0) {
                    channel.banks.map((bank: any) => {
                      const bankCascader: any = {
                        value: bank.id,
                        label: bank.name,
                        children: [],
                        type: "bank",
                        idOrganization: targetOrgNode.value,
                        idServer: serv.id,
                        idChannel: channel.id,
                        idBank: bank.id,
                        nameOrganization: targetOrgNode.label,
                        nameServer: serv.name,
                        fbEnabled: serv.fb_enabled,
                        nameChannel: channel.name,
                        typeChannel: channel.type,
                        socketUrl: serv.socket_url,
                        cmdBinaryControl: bank.cmd_binary_control || false,
                        ...bank,
                      };
                      return channelCascader.children.push(bankCascader);
                    });
                  }
                  return servCascader.children.push(channelCascader);
                });
              }
              return targetOrgNode.children.push(servCascader);
            }
            return true;
          });
        }
        return true;
      });
      setCascaderData(newArr);
      // Seed door-unknown toggle from the logged-in user's org
      if (userLogged && userLogged.org_id) {
        const userOrg = dataGetOrganizationsCascader.organizations.find(
          (org: any) => org.id === userLogged.org_id
        );
        if (userOrg) {
          dispatch(
            updateDoorUnknownAsOpening(userOrg.door_unknown_as_opening || false)
          );
          dispatch(
            updateHideUnconfiguredDoors(
              userOrg.hide_unconfigured_doors !== false
            )
          );
        }
      }
    }
  };

  const onChangeCascader = (_: any, selectedOptions: any) => {
    const newArrSelectedOptions: any = [];
    const newArrSelectedBanks: any = [];
    if (selectedOptions) {
      selectedOptions.map((item: any) => {
        // Build cascader value array
        const subArr: any = [];
        item.map((item2: any) => subArr.push(item2.value));
        newArrSelectedOptions.push(subArr);
        // Get all banks selected
        if (item && item.length === 4) {
          newArrSelectedBanks.push(item[3]);
        }
        return true;
      });
    }
    dispatch(updateOrganizationSelectMultipleData(newArrSelectedBanks));
    dispatch(
      updateCascaderSelectMultipleSelectionOptions(newArrSelectedOptions)
    );
    setCascaderValue(newArrSelectedOptions);
    persistSelection(newArrSelectedBanks, newArrSelectedOptions);
  };

  /** Filter grid data against the user's visible org for non-master users */
  const filterGridByOrg = (jsonObject: any) => {
    if (globalOrganizationId === process.env.REACT_APP_MASTER_ORG_ID) {
      return jsonObject;
    }
    const filteredPaths = (
      jsonObject.cascaderSelectMultipleSelectionOptions || []
    ).filter(
      (path: any[]) => path.length > 0 && path[0] === globalOrganizationId
    );
    const allowedBankIds = new Set(
      filteredPaths
        .filter((path: any[]) => path.length === 4)
        .map((path: any[]) => path[3])
    );
    const filteredBanks = (
      jsonObject.organizationSelectMultipleData || []
    ).filter(
      (bank: any) =>
        allowedBankIds.has(bank.idBank) || allowedBankIds.has(bank.id)
    );
    return {
      ...jsonObject,
      organizationSelectMultipleData: filteredBanks,
      cascaderSelectMultipleSelectionOptions: filteredPaths,
    };
  };

  const onClickFavoriteGrids = (grid: any) => {
    const rawJsonObject = JSON.parse(grid.grid);
    const jsonObject = filterGridByOrg(rawJsonObject);
    dispatch(updateIdGridSelected(grid.id));
    dispatch(updateTopViewLayout(jsonObject.topViewLayout));
    dispatch(updateDetailViewLayout(jsonObject.detailViewLayout));
    dispatch(updateStatusViewLayout(jsonObject.statusViewLayout));
    dispatch(
      updateOrganizationSelectMultipleData(
        jsonObject.organizationSelectMultipleData
      )
    );
    dispatch(
      updateCascaderSelectMultipleSelectionOptions(
        jsonObject.cascaderSelectMultipleSelectionOptions
      )
    );
    setCascaderValue(jsonObject.cascaderSelectMultipleSelectionOptions);
    persistSelection(
      jsonObject.organizationSelectMultipleData,
      jsonObject.cascaderSelectMultipleSelectionOptions
    );
    // Map view flags
    if (jsonObject.mapViewConfiguration) {
      dispatch(
        updateFloorsEnabled(jsonObject.mapViewConfiguration.floorsEnabled)
      );
      dispatch(
        updateNamesEnabled(jsonObject.mapViewConfiguration.namesEnabled)
      );
      dispatch(
        updateShowOriginal(jsonObject.mapViewConfiguration.showOriginal)
      );
      dispatch(
        updateServiceModeEnabled(
          jsonObject.mapViewConfiguration.serviceModeEnabled
        )
      );
    } else {
      dispatch(updateFloorsEnabled(false));
      dispatch(updateNamesEnabled(false));
      dispatch(updateShowOriginal(false));
      dispatch(updateServiceModeEnabled(false));
    }
  };

  /** Select all banks visible to the current user */
  const selectAllBanks = () => {
    const visible = getVisibleCascaderData(cascaderData);
    const allBanks: any[] = [];
    const allPaths: any[] = [];
    visible.forEach((org: any) => {
      if (org.children) {
        org.children.forEach((serv: any) => {
          if (serv.children) {
            serv.children.forEach((chan: any) => {
              if (chan.children) {
                chan.children.forEach((bank: any) => {
                  allPaths.push([
                    org.value,
                    serv.value,
                    chan.value,
                    bank.value,
                  ]);
                  allBanks.push(bank);
                });
              }
            });
          }
        });
      }
    });
    dispatch(updateOrganizationSelectMultipleData(allBanks));
    dispatch(updateCascaderSelectMultipleSelectionOptions(allPaths));
    setCascaderValue(allPaths);
    persistSelection(allBanks, allPaths);
  };

  // Track whether the grid load was triggered by an explicit user click
  const userClickedGrid = useRef(false);

  const getGrid = async (idGrid: string, isUserClick = false) => {
    userClickedGrid.current = isUserClick;
    const token = await GETJwtToken();
    triggerGetGridById({ token, idGrid });
  };

  // ************************************************* */
  // USE EFFECT ************************************** */
  useEffect(() => {
    initData();
  }, []);

  useEffect(() => {
    if (refreshPage) {
      initData();
    }
  }, [refreshPage]);

  useEffect(() => {
    if (dataGetOrganizationsCascader) {
      buildCascaderArray();
    }
  }, [dataGetOrganizationsCascader]);

  useEffect(() => {
    if (dataGetFavoriteGrids && dataGetFavoriteGrids.data) {
      setListFavoriteGrids(dataGetFavoriteGrids.data);
    }
  }, [dataGetFavoriteGrids]);

  useEffect(() => {
    if (
      userLogged &&
      userLogged.pref_grid_id &&
      !(
        cascaderSelectMultipleSelectionOptions &&
        cascaderSelectMultipleSelectionOptions.length > 0
      )
    ) {
      getGrid(userLogged.pref_grid_id);
    }
  }, [userLogged]);

  useEffect(() => {
    if (dataGridById && dataGridById.data) {
      // Always apply when user explicitly clicked a saved grid.
      // Only skip auto-load from pref_grid_id when a selection already exists.
      const hasSelection =
        cascaderSelectMultipleSelectionOptions &&
        cascaderSelectMultipleSelectionOptions.length > 0;
      if (userClickedGrid.current || !hasSelection) {
        dispatch(updateFavoriteGridName(dataGridById.data.name || ""));
        onClickFavoriteGrids(dataGridById.data);
      }
      userClickedGrid.current = false;
    }
  }, [dataGridById]);

  // Restore selection from localStorage after cascaderData is built
  useEffect(() => {
    if (cascaderData.length === 0) return;
    // Skip if there's already an active selection (in-session navigation)
    if (
      cascaderSelectMultipleSelectionOptions &&
      cascaderSelectMultipleSelectionOptions.length > 0
    ) {
      return;
    }
    // Skip if user has a pref_grid_id (that path handles its own restore)
    if (userLogged && userLogged.pref_grid_id) return;

    try {
      const raw = localStorage.getItem(CASCADER_STORAGE_KEY);
      if (!raw) return;
      const stored = JSON.parse(raw);
      // Only restore if same user
      if (!userLogged || stored.userId !== userLogged.id) return;

      const validBankIds = collectBankIds(getVisibleCascaderData(cascaderData));
      // Filter stored paths to only include banks that still exist and are visible
      const validPaths = (
        stored.cascaderSelectMultipleSelectionOptions || []
      ).filter((path: any[]) => path.length === 4 && validBankIds.has(path[3]));
      if (validPaths.length === 0) return;

      const validBankIdsFromPaths = new Set(validPaths.map((p: any[]) => p[3]));
      const validBanks = (stored.organizationSelectMultipleData || []).filter(
        (bank: any) =>
          validBankIdsFromPaths.has(bank.idBank) ||
          validBankIdsFromPaths.has(bank.id)
      );

      dispatch(updateOrganizationSelectMultipleData(validBanks));
      dispatch(updateCascaderSelectMultipleSelectionOptions(validPaths));
      setCascaderValue(validPaths);
    } catch {
      // Corrupted localStorage — ignore
    }
  }, [cascaderData]);

  useEffect(() => {
    if (clearCascader) {
      onChangeCascader(null, []);
      localStorage.removeItem(CASCADER_STORAGE_KEY);
      dispatch(updateClearCascader(false));
    }
  }, [clearCascader]);

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <div className="generalStyles__flex">
      {/** ******************************************* */}
      {/** CASCADER */}
      <div className="generalStyles__mlFix">
        <Cascader
          maxTagCount="responsive"
          options={getVisibleCascaderData(cascaderData)}
          onChange={onChangeCascader}
          placeholder="Please select"
          multiple
          value={cascaderValue}
          showCheckedStrategy={SHOW_CHILD}
          style={{ width: "350px" }}
          loading={
            isLoadingOrganizationCascader ||
            isLoadingFavoriteGrids ||
            isLoadingGridById
          }
        />
      </div>
      {/** SELECT ALL BUTTON */}
      <Tooltip title={t("monitoring.selectAll")}>
        <Button onClick={selectAllBanks} icon={<FontAwesomeIcon icon={faCheckDouble} />} />
      </Tooltip>
      <div className={`monitoringView__headerTagContainer__${theme}`}>
        <div>
          <div>
            <Dropdown
              menu={{
                items: (listFavoriteGrids ?? []).map((grid: any) => ({
                  key: grid.id,
                  label: (
                    <div>
                      {userLogged &&
                        userLogged.pref_grid_id &&
                        userLogged.pref_grid_id === grid.id && (
                          <FontAwesomeIcon
                            className="generalStyles__warning generalStyles__mrFix"
                            icon={faStar}
                          />
                        )}
                      <span
                        className={`${
                          userLogged &&
                          userLogged.pref_grid_id &&
                          userLogged.pref_grid_id === grid.id
                            ? ""
                            : "generalStyles__mlFix25"
                        }`}
                      >
                        {grid.name}
                      </span>
                    </div>
                  ),
                  onClick: () => getGrid(grid.id, true),
                })),
              }}
              placement="bottomLeft"
            >
              <Tooltip
                title={
                  listFavoriteGrids && listFavoriteGrids.length > 0
                    ? ""
                    : t("monitoring.noGridsFound")
                }
              >
                <FontAwesomeIcon
                  style={{
                    fontSize: "20px",
                    textAlign: "center",
                    paddingLeft: "10px",
                    paddingTop: "4px",
                  }}
                  className={`generalStyles__themeIcon1__${theme} ${
                    listFavoriteGrids && listFavoriteGrids.length > 0 ? "" : ""
                  }`}
                  icon={
                    listFavoriteGrids && listFavoriteGrids.length > 0
                      ? faList
                      : faBan
                  }
                />
              </Tooltip>
            </Dropdown>
          </div>
        </div>
      </div>
    </div>
  );
}

export default OrganizationCascaderSelectMultiple;
